import gsap from 'gsap';

const els = {
    $sliders: () => $('[data-testimonial-slider]'),
    $stage: ($slider) => $slider.find('[data-testimonial-slider-stage]'),
    $copy: ($slider) => $slider.find('.testimonial-slider__copy'),
    $quoteList: ($slider) => $slider.find('[data-testimonial-slider-quotes]'),
    $quoteGroups: ($slider) => $slider.find('[data-testimonial-slider-quote-group]'),
    $quotes: ($slider) => $slider.find('[data-testimonial-slider-quote]'),
    $attributions: ($slider) => $slider.find('[data-testimonial-slider-attribution]'),
    $imageTrack: ($slider) => $slider.find('[data-testimonial-slider-image-track]'),
    $images: ($slider) => $slider.find('[data-testimonial-slider-image]'),
    $indicators: ($slider) => $slider.find('[data-testimonial-slider-indicator]'),
    $counter: ($slider) => $slider.find('[data-testimonial-slider-counter]'),
};

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const HOVER_QUERY = '(any-hover: hover)';
const LARGE_VIEWPORT_QUERY = '(min-width: 64rem)';
const MAX_HOVER_PAUSE_HEIGHT_RATIO = 0.9;
const MIN_DELAY = 3500;
const MAX_DELAY = 9000;
const BASE_DELAY = 2500;
const WORD_DELAY = 220;
const RESIZE_DELAY = 150;
const LINE_TOP_TOLERANCE = 2;
const SWIPE_MIN_DISTANCE = 40;
const SWIPE_DIRECTION_TOLERANCE = 1.25;

const prefersReducedMotion = () => (
    window.matchMedia(REDUCED_MOTION_QUERY).matches
);

const supportsHover = () => (
    window.matchMedia(HOVER_QUERY).matches
);

const isLargeViewport = () => (
    window.matchMedia(LARGE_VIEWPORT_QUERY).matches
);

const canUseHoverPause = (state) => (
    supportsHover()
    && isLargeViewport()
    && state.slider.getBoundingClientRect().height < window.innerHeight * MAX_HOVER_PAUSE_HEIGHT_RATIO
);

const clamp = (min, max, value) => Math.min(max, Math.max(min, value));

const getWords = (text) => (
    (text || '').trim().split(/\s+/).filter(Boolean)
);

const getSlideDelay = (quote, attribution) => {
    const wordCount = getWords(
        `${quote?.textContent || ''} ${attribution?.textContent || ''}`
    ).length;

    return clamp(MIN_DELAY, MAX_DELAY, BASE_DELAY + (wordCount * WORD_DELAY));
};

const getSlideWidth = (state) => (
    state.image?.getBoundingClientRect().width || 0
);

const getNextIndex = (state) => (
    (state.activeIndex + 1) % state.quoteGroups.length
);

const getPreviousIndex = (state) => (
    state.activeIndex === 0 ? state.quoteGroups.length - 1 : state.activeIndex - 1
);

const getImageX = (state, index) => (
    state.imageTrack ? getSlideWidth(state) * -index : 0
);

const getWidestRenderedLineWidth = (element) => {
    if (!element) {
        return 0;
    }

    const range = document.createRange();
    range.selectNodeContents(element);

    const lines = [];

    Array.from(range.getClientRects()).forEach((rect) => {
        if (rect.width <= 0 || rect.height <= 0) {
            return;
        }

        const line = lines.find((candidate) => (
            Math.abs(candidate.top - rect.top) <= LINE_TOP_TOLERANCE
        ));

        if (line) {
            line.top = Math.min(line.top, rect.top);
            line.left = Math.min(line.left, rect.left);
            line.right = Math.max(line.right, rect.right);
            return;
        }

        lines.push({
            top: rect.top,
            left: rect.left,
            right: rect.right,
        });
    });

    range.detach();

    return lines.reduce((maxWidth, line) => (
        Math.max(maxWidth, line.right - line.left)
    ), 0);
};

const updateControls = (state, nextIndex, shouldFocus = false) => {
    state.$indicators.each(function(index) {
        const isActive = index === nextIndex;
        const $indicator = $(this);

        $indicator.attr({
            'aria-selected': isActive ? 'true' : 'false',
            tabindex: isActive ? '0' : '-1',
        });

        if (isActive && shouldFocus) {
            this.focus();
        }
    });

    if (state.$counter.length) {
        state.$counter.text(`${String(nextIndex + 1).padStart(2, '0')} / ${String(state.quoteGroups.length).padStart(2, '0')}`);
    }
};

const setActiveContent = (state, index) => {
    state.$quoteGroups.each(function(groupIndex) {
        const isActive = groupIndex === index;
        const group = this;

        $(group).attr('aria-hidden', isActive ? 'false' : 'true');
        gsap.set(group, {
            autoAlpha: isActive ? 1 : 0,
            position: isActive ? 'relative' : 'absolute',
            y: 0,
        });
    });
};

const measureFixedRegions = (state) => {
    if (!state.stage) {
        return;
    }

    const activeIndex = state.activeIndex;
    let maxQuoteGroupHeight = 0;
    state.quoteGroupHeights = [];
    state.stage.style.removeProperty('--testimonial-slider-stage-height');

    state.$quoteGroups.each(function() {
        this.style.removeProperty('--testimonial-slider-measured-quote-width');
    });

    state.$quoteGroups.each(function(index) {
        const group = this;
        const quote = state.quotes[index];
        const quoteText = quote ? $(quote).find('p').first()[0] || quote : null;

        gsap.set(group, {
            clearProps: 'visibility,opacity,transform',
        });
        gsap.set(group, { position: 'relative', autoAlpha: 1 });

        const measuredQuoteWidth = Math.ceil(
            getWidestRenderedLineWidth(quoteText)
        );

        if (measuredQuoteWidth > 0) {
            group.style.setProperty(
                '--testimonial-slider-measured-quote-width',
                `${measuredQuoteWidth}px`
            );
        }

        const quoteGroupHeight = group.offsetHeight;

        state.quoteGroupHeights[index] = quoteGroupHeight;
        maxQuoteGroupHeight = Math.max(maxQuoteGroupHeight, quoteGroupHeight);
    });

    state.copy?.style.setProperty(
        '--testimonial-slider-copy-height',
        `${Math.ceil(maxQuoteGroupHeight)}px`
    );
    state.quoteList?.style.setProperty(
        '--testimonial-slider-quote-height',
        `${Math.ceil(maxQuoteGroupHeight)}px`
    );

    setActiveContent(state, activeIndex);
    state.stage.style.setProperty(
        '--testimonial-slider-stage-height',
        `${Math.ceil(state.stage.offsetHeight)}px`
    );
};

const clearTimer = (state) => {
    if (state.timer) {
        window.clearTimeout(state.timer);
        state.timer = null;
    }
};

const scheduleNext = (state) => {
    clearTimer(state);

    if (
        state.isPaused
        || state.quoteGroups.length < 2
        || prefersReducedMotion()
    ) {
        return;
    }

    state.timer = window.setTimeout(() => {
        activateSlide(state, (state.activeIndex + 1) % state.quoteGroups.length, {
            isAutoplay: true,
        });
    }, getSlideDelay(
        state.quotes[state.activeIndex],
        state.attributions[state.activeIndex]
    ));
};

function activateSlide(state, nextIndex, options = {}) {
    const {
        isAutoplay = false,
        shouldFocus = false,
    } = options;
    const previousIndex = state.activeIndex;

    if (nextIndex === previousIndex || !state.quoteGroups[nextIndex]) {
        updateControls(state, nextIndex, shouldFocus);
        scheduleNext(state);
        return;
    }

    clearTimer(state);
    state.activeIndex = nextIndex;
    updateControls(state, nextIndex, shouldFocus);

    const outgoingGroup = state.quoteGroups[previousIndex];
    const incomingGroup = state.quoteGroups[nextIndex];
    const direction = nextIndex > previousIndex ? 1 : -1;
    const isReset = isAutoplay && previousIndex === state.quoteGroups.length - 1 && nextIndex === 0;
    const imageX = getImageX(state, nextIndex);

    gsap.killTweensOf([
        outgoingGroup,
        incomingGroup,
        state.imageTrack,
    ].filter(Boolean));

    if (prefersReducedMotion()) {
        setActiveContent(state, nextIndex);
        if (state.imageTrack) {
            gsap.set(state.imageTrack, { x: imageX });
        }
        scheduleNext(state);
        return;
    }

    $(incomingGroup).attr('aria-hidden', 'false');

    gsap.set(incomingGroup, {
        autoAlpha: 0,
        position: 'absolute',
        y: 10 * direction,
    });

    const timeline = gsap.timeline({
        onComplete: () => {
            $(outgoingGroup).attr('aria-hidden', 'true');
            setActiveContent(state, nextIndex);
            scheduleNext(state);
        },
    });

    timeline
        .to(outgoingGroup, {
            duration: 0.28,
            ease: 'power1.out',
            autoAlpha: 0,
            y: -10 * direction,
        }, 0)
        .to(incomingGroup, {
            duration: 0.34,
            ease: 'power2.out',
            autoAlpha: 1,
            y: 0,
        }, 0.18);

    if (state.imageTrack) {
        timeline.to(state.imageTrack, {
            duration: isReset ? 0.72 : 0.52,
            ease: 'power2.inOut',
            x: imageX,
        }, 0);
    }
}

const navigateFromSwipe = (state, direction) => {
    if (state.quoteGroups.length < 2) {
        return;
    }

    state.isPaused = false;
    activateSlide(
        state,
        direction === 'next' ? getNextIndex(state) : getPreviousIndex(state)
    );
};

const initializeSwipeGestures = (state) => {
    if (!window.PointerEvent) {
        return;
    }

    let touchStart = null;

    state.$slider.on('pointerdown', (event) => {
        const pointerEvent = event.originalEvent || event;

        if (pointerEvent.pointerType !== 'touch' || !pointerEvent.isPrimary) {
            return;
        }

        touchStart = {
            pointerId: pointerEvent.pointerId,
            x: pointerEvent.clientX,
            y: pointerEvent.clientY,
        };
    });

    state.$slider.on('pointerup', (event) => {
        const pointerEvent = event.originalEvent || event;

        if (
            !touchStart
            || pointerEvent.pointerId !== touchStart.pointerId
            || pointerEvent.pointerType !== 'touch'
        ) {
            touchStart = null;
            return;
        }

        const deltaX = pointerEvent.clientX - touchStart.x;
        const deltaY = pointerEvent.clientY - touchStart.y;

        touchStart = null;

        if (
            Math.abs(deltaX) < SWIPE_MIN_DISTANCE
            || Math.abs(deltaX) < Math.abs(deltaY) * SWIPE_DIRECTION_TOLERANCE
        ) {
            return;
        }

        navigateFromSwipe(state, deltaX < 0 ? 'next' : 'previous');
    });

    state.$slider.on('pointercancel', (event) => {
        const pointerEvent = event.originalEvent || event;

        if (touchStart?.pointerId === pointerEvent.pointerId) {
            touchStart = null;
        }
    });
};

const initializeHoverPause = (state) => {
    if (!window.PointerEvent || !supportsHover()) {
        return;
    }

    state.$slider.on('pointerenter', (event) => {
        const pointerEvent = event.originalEvent || event;

        if (pointerEvent.pointerType === 'touch' || !canUseHoverPause(state)) {
            return;
        }

        state.isHoverPaused = true;
        state.isPaused = true;
        clearTimer(state);
    });

    state.$slider.on('pointerleave', (event) => {
        const pointerEvent = event.originalEvent || event;

        if (pointerEvent.pointerType === 'touch') {
            return;
        }

        state.isHoverPaused = false;

        if (document.activeElement && state.slider.contains(document.activeElement)) {
            return;
        }

        state.isPaused = false;
        scheduleNext(state);
    });
};

const initializeSlider = ($slider) => {
    const $stage = els.$stage($slider).first();
    const $copy = els.$copy($slider).first();
    const $quoteList = els.$quoteList($slider).first();
    const $quoteGroups = els.$quoteGroups($slider);
    const $quotes = els.$quotes($slider);
    const $attributions = els.$attributions($slider);
    const $imageTrack = els.$imageTrack($slider).first();
    const $images = els.$images($slider);
    const $indicators = els.$indicators($slider);
    const $counter = els.$counter($slider).first();

    const state = {
        $slider,
        slider: $slider[0],
        stage: $stage[0],
        copy: $copy[0],
        quoteList: $quoteList[0],
        $quoteGroups,
        quoteGroups: $quoteGroups.toArray(),
        quotes: $quotes.toArray(),
        attributions: $attributions.toArray(),
        imageTrack: $imageTrack[0],
        image: $images.first()[0],
        $indicators,
        $counter,
        activeIndex: 0,
        isPaused: false,
        isHoverPaused: false,
        timer: null,
        quoteGroupHeights: [],
    };

    if (!state.stage || state.quoteGroups.length === 0) {
        return;
    }

    setActiveContent(state, 0);
    measureFixedRegions(state);
    if (state.imageTrack) {
        gsap.set(state.imageTrack, { x: getImageX(state, 0) });
    }
    initializeSwipeGestures(state);
    initializeHoverPause(state);

    state.$indicators.each(function(index) {
        $(this).on('click', () => {
            if (index === state.activeIndex) {
                return;
            }

            state.isPaused = false;
            activateSlide(state, index, { shouldFocus: true });
        });

        $(this).on('keydown', (event) => {
            let nextIndex = null;

            if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                nextIndex = index === 0 ? state.$indicators.length - 1 : index - 1;
            }

            if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                nextIndex = index === state.$indicators.length - 1 ? 0 : index + 1;
            }

            if (event.key === 'Home') {
                nextIndex = 0;
            }

            if (event.key === 'End') {
                nextIndex = state.$indicators.length - 1;
            }

            if (nextIndex !== null) {
                event.preventDefault();
                state.isPaused = false;
                activateSlide(state, nextIndex, { shouldFocus: true });
            }
        });
    });

    state.$slider.on('focusin', () => {
        state.isPaused = true;
        clearTimer(state);
    });

    state.$slider.on('focusout', (event) => {
        if (event.relatedTarget && state.slider.contains(event.relatedTarget)) {
            return;
        }

        if (state.isHoverPaused) {
            return;
        }

        state.isPaused = false;
        scheduleNext(state);
    });

    let resizeTimer;

    $(window).on('resize:horizontally', () => {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(() => {
            measureFixedRegions(state);
            if (state.imageTrack) {
                gsap.set(state.imageTrack, {
                    x: getImageX(state, state.activeIndex),
                });
            }

            if (state.isHoverPaused && !canUseHoverPause(state)) {
                state.isHoverPaused = false;

                if (!document.activeElement || !state.slider.contains(document.activeElement)) {
                    state.isPaused = false;
                    scheduleNext(state);
                }
            }
        }, RESIZE_DELAY);
    });

    scheduleNext(state);
};

const initialize = () => {
    els.$sliders().each(function() {
        initializeSlider($(this));
    });
};

export default {
    initialize,
};
